// src/pages/Builders/PlotPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../../layout/builders/Layout";
import LocationAutocomplete from "../../components/LocationAutocomplete";
import LocationPicker from "../../components/LocationPicker";
import CustomerEnquiries from "../../layout/builders/CustomerEnquiry/CustomerEnquiries";
import { axiosInstancev1 } from "../../lib/axiosInstance";
import toast from "react-hot-toast";
import RichTextEditor from "../../components/common/RichTextEditor";
import DurationSelectionModal from "../../layout/realestateagent/HousingPost/DurationSelectionModal";
import PostedProperties from "../../layout/realestateagent/HousingPost/PostedProperties";
import {
  MapPinIcon,
  BanknotesIcon,
  ArrowsPointingOutIcon,
  PhotoIcon,
  ShieldCheckIcon,
  BoltIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  ClockIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  TrashIcon,
  HomeIcon,
  Squares2X2Icon,
  DocumentCheckIcon,
} from "@heroicons/react/24/outline";

// ─── Constants ────────────────────────────────────────────────────────────────
const TOTAL_STEPS = 6;

const PLOT_TYPES = [
  { id: "Residential Plot", label: "Residential Plot" },
  { id: "Commercial Plot", label: "Commercial Plot" },
  { id: "Industrial Plot", label: "Industrial Plot" },
];

const FACING = ["East", "West", "North", "South", "NE", "NW", "SE", "SW"];

const APPROVAL_STATUS = ["DTCP Approved", "CMDA Approved", "RERA Registered"];

const AMENITIES = [
  "Gated Community",
  "24x7 Water Supply",
  "Power Supply",
  "Sewage Connection",
  "Road Access",
  "Security",
  "Park / Green Area",
  "Street Lights",
  "Drainage System",
  "Compound Wall",
];

const STEPS_META = [
  { id: 1, label: "Basic & Approvals", title: "Basic Details & Approvals", desc: "Core info and approval status." },
  { id: 2, label: "Specifications", title: "Plot Specifications", desc: "Plot Specifications" },
  { id: 3, label: "Media", title: "Media Upload", desc: "Media Upload" },
  { id: 4, label: "Additional", title: "Additional Information", desc: "Additional Information" },
  { id: 5, label: "Location", title: "Location Details", desc: "Location Details" },
  { id: 6, label: "Contact", title: "Contact Details", desc: "Contact Details" },
];

// ─── Sub-components ───────────────────────────────────────────────────────────
const GradientHeader = ({ step, totalSteps }) => (
  <div className="rounded-3xl bg-gradient-to-r from-blue-50 via-sky-100 to-blue-100 px-5 py-4">
    <div className="flex items-center justify-between gap-3">
      <div className="flex-1">
        <p className="text-xs uppercase tracking-wide text-blue-700">
          Step {step} of {totalSteps}
        </p>
        <div className="mt-1 h-1.5 w-full bg-white/60 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-500 rounded-full transition-all duration-300"
            style={{ width: `${(step / totalSteps) * 100}%` }}
          />
        </div>
      </div>
    </div>
  </div>
);

const StepCard = ({ stepNumber, title, description, children, isOpen, toggle }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm transition-all duration-300 hover:shadow-md hover:border-blue-100 hover:-translate-y-[1px]">
    <button
      type="button"
      onClick={toggle}
      className="w-full flex items-center justify-between px-4 py-3 sm:px-5 sm:py-4"
    >
      <div className="text-left">
        <h2 className="text-sm sm:text-base font-semibold text-gray-900">{title}</h2>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
      <ChevronDownIcon
        className={`w-4 h-4 text-gray-500 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
      />
    </button>
    <div className={`border-t border-gray-100 overflow-hidden transition-all duration-300 ${isOpen ? "max-h-[1400px] opacity-100" : "max-h-0 opacity-0"}`}>
      <div className="px-4 py-4 sm:px-5 sm:py-5">{children}</div>
    </div>
  </div>
);

// ─── Main PlotForm component ──────────────────────────────────────────────────
const PlotForm = () => {
  const [activeTab, setActiveTab] = useState("post");
  const [step, setStep] = useState(1);
  const { id } = useParams();
  const navigate = useNavigate();

  const emptyForm = {
    // Step 1 — Basic & Approvals
    plotTitle: "",
    listingType: "Sale",
    plotType: "",
    approvalStatus: "",
    expectedPrice: "",
    priceNegotiable: "Yes",
    // Step 2 — Specifications
    plotArea: "",
    plotWidth: "",
    plotDepth: "",
    facing: "",
    roadWidth: "",
    // Step 4 — Additional
    description: "",
    highlights: "",
    nearbyLocations: "",
    // Step 5 — Location
    city: "",
    locality: "",
    streetAddress: "",
    landmark: "",
    pincode: "",
    mapUrl: "",
    latitude: null,
    longitude: null,
    // Step 6 — Contact
    ownerName: "",
    mobileNumber: "",
    altNumber: "",
    email: "",
    preferredContactTime: "",
  };

  const [form, setForm] = useState(emptyForm);
  const [amenities, setAmenities] = useState([]);
  const [images, setImages] = useState([]);
  const [deletedImageIds, setDeletedImageIds] = useState([]);
  const [errors, setErrors] = useState({});
  const [openSections, setOpenSections] = useState({ 1: true, 2: true, 3: true, 4: true, 5: true, 6: true });
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showDurationModal, setShowDurationModal] = useState(false);
  const [createdListingId, setCreatedListingId] = useState(null);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const toggleAmenity = (a) => {
    setAmenities((prev) => {
      const s = new Set(prev);
      if (s.has(a)) s.delete(a); else s.add(a);
      return Array.from(s);
    });
  };

  const handleImages = (e) => {
    const files = Array.from(e.target.files || []);
    const valid = files.filter((f) => {
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} exceeds 10MB`); return false; }
      return true;
    });
    setImages((prev) => [...prev, ...valid.map((f) => ({ file: f, previewUrl: URL.createObjectURL(f) }))]);
  };

  const removeImage = (i) => {
    const item = images[i];
    if (item?.existing && item?.imageId) setDeletedImageIds((prev) => [...prev, item.imageId]);
    setImages((prev) => prev.filter((_, idx) => idx !== i));
  };

  const setAsMain = (i) => {
    if (i === 0) return;
    setImages((prev) => { const arr = [...prev]; const [sel] = arr.splice(i, 1); arr.unshift(sel); return arr; });
  };

  const validateStep = () => {
    const e = {};
    if (step === 1) {
      if (!form.plotTitle.trim()) e.plotTitle = "Plot title is required";
      if (!form.plotType) e.plotType = "Select plot type";
      if (!form.expectedPrice) e.expectedPrice = "Price is required";
    }
    if (step === 2) {
      if (!form.plotArea) e.plotArea = "Plot area is required";
    }
    if (step === 5) {
      if (!form.city.trim()) e.city = "City is required";
      if (!form.locality.trim()) e.locality = "Locality is required";
      if (!form.streetAddress.trim()) e.streetAddress = "Street address is required";
    }
    if (step === 6) {
      if (!form.ownerName.trim()) e.ownerName = "Owner name is required";
      if (!form.mobileNumber.trim()) e.mobileNumber = "Mobile number is required";
      else if (form.mobileNumber.length !== 10) e.mobileNumber = "Mobile number must be 10 digits";
      if (form.altNumber && form.altNumber.length !== 10) e.altNumber = "Alternate number must be 10 digits";
      if (!form.email.trim()) e.email = "Email is required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const goNext = () => { if (!validateStep()) return; setStep((p) => Math.min(p + 1, TOTAL_STEPS)); };
  const goBack = () => setStep((p) => Math.max(p - 1, 1));

  // Load existing plot for editing
  useEffect(() => {
    if (id) loadPlot(id);
  }, [id]);

  const loadPlot = async (plotId) => {
    try {
      setSubmitting(true);
      const res = await axiosInstancev1.get(`/properties/${plotId}`);
      const data = res.data;
      if (data.success) {
        const d = data.details || {};
        let ams = d.amenities;
        if (typeof ams === "string") { try { ams = JSON.parse(ams); } catch { ams = []; } }
        setForm({
          plotTitle: d.plot_title || data.plot_title || "",
          listingType: "Sale",
          plotType: d.plot_type || "",
          approvalStatus: d.approval_status || "",
          expectedPrice: d.expected_price || data.price || "",
          priceNegotiable: d.is_price_negotiable ? "Yes" : "No",
          plotArea: d.plot_area || "",
          plotWidth: d.plot_width || "",
          plotDepth: d.plot_depth || "",
          facing: d.facing_direction || "",
          roadWidth: d.road_width || "",
          description: d.description || "",
          highlights: d.highlights || "",
          nearbyLocations: d.nearby_locations || "",
          city: data.city || "",
          locality: data.locality || "",
          streetAddress: data.street_address || "",
          landmark: data.landmark || "",
          pincode: data.pincode || "",
          mapUrl: data.google_map_link || "",
          latitude: data.latitude || null,
          longitude: data.longitude || null,
          ownerName: data.contact_name || "",
          mobileNumber: data.contact_mobile || "",
          altNumber: data.contact_alt_mobile || "",
          email: data.contact_email || "",
          preferredContactTime: data.contact_time || "",
        });
        setAmenities(Array.isArray(ams) ? ams : []);
        if (data.images?.length) {
          setImages(data.images.map((img) => ({ previewUrl: img.image_url, file: null, existing: true, imageId: img.id })));
        }
        setEditingId(plotId);
        setActiveTab("post");
        setStep(1);
      }
    } catch (err) {
      toast.error("Failed to load plot details");
    } finally {
      setSubmitting(false);
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
    setAmenities([]);
    setImages([]);
    setStep(1);
    navigate("/builder/plot");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("listing_type", form.listingType);
      fd.append("property_category", "plot");
      fd.append("city", form.city);
      fd.append("locality", form.locality);
      fd.append("street_address", form.streetAddress);
      fd.append("landmark", form.landmark || "");
      fd.append("pincode", form.pincode || "");
      fd.append("google_map_link", form.mapUrl || "");
      if (form.latitude) fd.append("latitude", form.latitude);
      if (form.longitude) fd.append("longitude", form.longitude);
      fd.append("contact_name", form.ownerName);
      fd.append("contact_mobile", form.mobileNumber);
      fd.append("contact_alt_mobile", form.altNumber || "");
      fd.append("contact_email", form.email);
      fd.append("contact_time", form.preferredContactTime || "");

      const plotDetails = {
        plot_title: form.plotTitle,
        plot_type: form.plotType,
        approval_status: form.approvalStatus,
        expected_price: form.expectedPrice,
        price: form.expectedPrice,
        is_price_negotiable: form.priceNegotiable === "Yes" ? 1 : 0,
        plot_area: form.plotArea,
        plot_area_unit: "sq ft",
        plot_width: form.plotWidth,
        plot_depth: form.plotDepth,
        facing_direction: form.facing,
        road_width: form.roadWidth,
        amenities: amenities,
        description: form.description,
        highlights: form.highlights,
        nearby_locations: form.nearbyLocations,
        dtcp_approved: form.approvalStatus === "DTCP Approved" ? 1 : 0,
        cmda_approved: form.approvalStatus === "CMDA Approved" ? 1 : 0,
        rera_number: form.approvalStatus === "RERA Registered" ? "RERA" : "",
      };
      fd.append("plot_details", JSON.stringify(plotDetails));

      if (deletedImageIds.length > 0) fd.append("deleted_image_ids", JSON.stringify(deletedImageIds));

      if (images.length > 0) {
        const primary = images[0];
        if (primary.existing && primary.imageId) fd.append("primary_image_id", primary.imageId);
        else fd.append("new_primary_image_index", 0);
      }
      images.forEach((img) => { if (img.file) fd.append("images", img.file); });

      let res;
      if (editingId) {
        res = await axiosInstancev1.put(`/properties/${editingId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        res = await axiosInstancev1.post("/properties", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }

      if (res.data.success) {
        toast.success(editingId ? "Plot updated successfully!" : "Plot listing created successfully!");
        if (editingId) {
          cancelEdit();
          setActiveTab("posted");
        } else {
          setCreatedListingId(res.data.listingId);
          setShowDurationModal(true);
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to submit plot listing");
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="min-h-screen bg-white">
        <div className="w-full max-w-6xl mx-auto">

          {/* Tab nav */}
          <div className="mb-4 border-b border-gray-200 overflow-x-auto no-scrollbar">
            <nav className="-mb-px flex space-x-4 sm:space-x-6 min-w-max" aria-label="Tabs">
              {[
                { key: "post", label: "Post Plot" },
                { key: "posted", label: "Your Listings" },
                { key: "enquiry", label: "Enquiry Data" },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveTab(key)}
                  className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                    activeTab === key
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab content */}
          {activeTab === "enquiry" ? (
            <CustomerEnquiries category="plot" />
          ) : activeTab === "posted" ? (
            <PostedProperties onEdit={(p) => loadPlot(p?.id || p)} listingTypeFilter="Sale" />
          ) : (
            <form
              onSubmit={handleSubmit}
              className="bg-white rounded-[28px] border border-gray-200 shadow-sm px-5 py-5 sm:px-7 sm:py-7 lg:p-8 lg:rounded-3xl"
            >
              {/* Mobile gradient header */}
              <div className="mb-4 lg:hidden"><GradientHeader step={step} totalSteps={TOTAL_STEPS} /></div>

              <div className="flex flex-col lg:flex-row lg:gap-10">
                {/* LEFT SIDEBAR — desktop sticky step list */}
                <aside className="hidden lg:flex lg:flex-col lg:w-1/3 lg:pr-6 lg:border-r lg:border-gray-100 lg:sticky lg:top-6 lg:self-start">
                  <GradientHeader step={step} totalSteps={TOTAL_STEPS} />

                  <div className="mt-6 space-y-4">
                    <h1 className="text-xl font-semibold text-gray-900">
                      {STEPS_META.find((s) => s.id === step)?.title}
                    </h1>
                    <p className="text-sm text-gray-500">
                      {STEPS_META.find((s) => s.id === step)?.desc}
                    </p>
                  </div>

                  <ul className="mt-6 space-y-2">
                    {STEPS_META.map((s) => {
                      const isActive = s.id === step;
                      const isDone = s.id < step;
                      return (
                        <li key={s.id}>
                          <button
                            type="button"
                            onClick={() => setStep(s.id)}
                            className={`w-full flex items-center gap-3 rounded-xl px-3 py-2 text-left transition-all duration-300 ${
                              isActive
                                ? "bg-blue-50 text-blue-700 shadow-sm scale-[1.02]"
                                : isDone
                                ? "bg-green-50 text-green-700"
                                : "text-gray-600 hover:bg-gray-50"
                            }`}
                          >
                            <span
                              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
                                isActive
                                  ? "bg-blue-600 text-white"
                                  : isDone
                                  ? "bg-green-600 text-white"
                                  : "bg-gray-200 text-gray-700"
                              }`}
                            >
                              {isDone ? "✓" : s.id}
                            </span>
                            <div className="flex-1">
                              <p className="text-xs font-semibold">{s.label}</p>
                              <p className="text-[11px] text-gray-500 truncate">{s.title}</p>
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </aside>

                {/* RIGHT — step content */}
                <div className="w-full lg:w-2/3 lg:pl-6 space-y-5">

                  {/* ── STEP 1: Basic & Approvals ── */}
                  {step === 1 && (
                    <StepCard
                      stepNumber={1}
                      title="Basic Details & Approvals"
                      description="Core info and approval status."
                      isOpen={openSections[1]}
                      toggle={() => setOpenSections((p) => ({ ...p, 1: !p[1] }))}
                    >
                      <div className="space-y-4">
                        {/* Plot Title */}
                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-2">Plot Title</p>
                          <input
                            type="text"
                            className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                            placeholder="e.g. Premium DTCP Plot near bypass road"
                            value={form.plotTitle}
                            onChange={(e) => updateField("plotTitle", e.target.value)}
                          />
                          {errors.plotTitle && <p className="text-[11px] text-red-500 mt-1">{errors.plotTitle}</p>}
                        </div>

                        {/* Plot Type */}
                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-2">Plot Type</p>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {PLOT_TYPES.map((pt) => {
                              const active = form.plotType === pt.id;
                              return (
                                <button
                                  key={pt.id}
                                  type="button"
                                  onClick={() => updateField("plotType", pt.id)}
                                  className={`flex flex-col items-center justify-center rounded-2xl border px-3 py-4 text-xs font-medium transition-all ${
                                    active
                                      ? "border-blue-500 bg-blue-50 text-blue-700 shadow-sm"
                                      : "border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100"
                                  }`}
                                >
                                  <Squares2X2Icon className="w-6 h-6 text-blue-500 mb-1.5" />
                                  {pt.label}
                                </button>
                              );
                            })}
                          </div>
                          {errors.plotType && <p className="text-[11px] text-red-500 mt-1">{errors.plotType}</p>}
                        </div>

                        {/* Approval Status */}
                        <div>
                          <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-2">
                            <DocumentCheckIcon className="w-4 h-4 text-blue-600" />
                            Approval Status
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {APPROVAL_STATUS.map((s) => {
                              const active = form.approvalStatus === s;
                              return (
                                <button
                                  key={s}
                                  type="button"
                                  onClick={() => updateField("approvalStatus", s)}
                                  className={`px-3 py-1.5 rounded-full text-xs border transition-all ${
                                    active ? "bg-blue-600 text-white border-blue-600" : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
                                  }`}
                                >
                                  {s}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Price */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5">
                              <BanknotesIcon className="w-4 h-4 text-blue-600" />
                              Expected Price (₹)
                            </p>
                            <input
                              type="number"
                              className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                              placeholder="e.g. 2500000"
                              value={form.expectedPrice}
                              onChange={(e) => updateField("expectedPrice", e.target.value)}
                            />
                            {errors.expectedPrice && <p className="text-[11px] text-red-500 mt-1">{errors.expectedPrice}</p>}
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1.5">Price Negotiable</p>
                            <div className="flex gap-2">
                              {["Yes", "No"].map((opt) => (
                                <button
                                  key={opt}
                                  type="button"
                                  onClick={() => updateField("priceNegotiable", opt)}
                                  className={`flex-1 px-3 py-2 rounded-2xl text-xs border transition-all ${
                                    form.priceNegotiable === opt
                                      ? "bg-green-50 border-green-500 text-green-700"
                                      : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100"
                                  }`}
                                >
                                  {opt}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </StepCard>
                  )}

                  {/* ── STEP 2: Specifications ── */}
                  {step === 2 && (
                    <StepCard
                      stepNumber={2}
                      title="Plot Specifications"
                      description="Dimensions and physical details of the plot."
                      isOpen={openSections[2]}
                      toggle={() => setOpenSections((p) => ({ ...p, 2: !p[2] }))}
                    >
                      <div className="space-y-5">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div>
                            <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5">
                              <ArrowsPointingOutIcon className="w-4 h-4 text-blue-600" />
                              Total Area (sq ft) *
                            </p>
                            <input
                              type="number"
                              className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                              value={form.plotArea}
                              onChange={(e) => updateField("plotArea", e.target.value)}
                            />
                            {errors.plotArea && <p className="text-[11px] text-red-500 mt-1">{errors.plotArea}</p>}
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1.5">Width (ft)</p>
                            <input
                              type="number"
                              className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                              value={form.plotWidth}
                              onChange={(e) => updateField("plotWidth", e.target.value)}
                            />
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1.5">Depth (ft)</p>
                            <input
                              type="number"
                              className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                              value={form.plotDepth}
                              onChange={(e) => updateField("plotDepth", e.target.value)}
                            />
                          </div>
                        </div>

                        {/* Road Width */}
                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-1.5">Road Width (ft)</p>
                          <input
                            type="number"
                            className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                            placeholder="e.g. 30"
                            value={form.roadWidth}
                            onChange={(e) => updateField("roadWidth", e.target.value)}
                          />
                        </div>

                        {/* Facing */}
                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-2">Facing</p>
                          <div className="flex flex-wrap gap-2">
                            {FACING.map((f) => (
                              <button
                                key={f}
                                type="button"
                                onClick={() => updateField("facing", f)}
                                className={`px-3 py-1.5 rounded-full text-xs border transition-all ${
                                  form.facing === f
                                    ? "bg-blue-600 text-white border-blue-600"
                                    : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
                                }`}
                              >
                                {f}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </StepCard>
                  )}

                  {/* ── STEP 3: Media ── */}
                  {step === 3 && (
                    <StepCard
                      stepNumber={3}
                      title="Media Upload"
                      description="Add photos of your plot to attract buyers."
                      isOpen={openSections[3]}
                      toggle={() => setOpenSections((p) => ({ ...p, 3: !p[3] }))}
                    >
                      <div className="space-y-4">
                        {/* Upload area */}
                        <div>
                          <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5">
                            <PhotoIcon className="w-4 h-4 text-blue-600" />
                            Plot Photos
                          </p>
                          <p className="text-[11px] text-gray-500 mb-2">Add clear photos: plot boundaries, road access, surroundings.</p>
                          <label className="block">
                            <div className="w-full rounded-2xl border-2 border-dashed border-blue-200 bg-blue-50/60 px-4 py-8 text-center text-xs text-blue-700 cursor-pointer hover:bg-blue-50 transition-all">
                              <p className="font-medium mb-1">Tap to upload or drag & drop</p>
                              <p>JPEG, PNG — multiple files allowed (max 10MB each)</p>
                            </div>
                            <input type="file" multiple accept="image/*" className="hidden" onChange={handleImages} />
                          </label>
                        </div>

                        {/* Preview grid */}
                        {images.length > 0 && (
                          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2">
                            {images.map((img, i) => (
                              <div
                                key={i}
                                onClick={() => setAsMain(i)}
                                className={`relative overflow-hidden rounded-xl border-2 cursor-pointer transition-all ${
                                  i === 0 ? "border-blue-500 shadow-md" : "border-gray-100 hover:border-blue-300"
                                }`}
                              >
                                <img src={img.previewUrl} alt={`Plot ${i + 1}`} className="w-full h-20 object-cover" />
                                {i === 0 && (
                                  <div className="absolute top-1 left-1 bg-blue-600 text-[8px] text-white px-2 py-0.5 rounded-full font-bold">
                                    Banner
                                  </div>
                                )}
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); removeImage(i); }}
                                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 shadow hover:bg-red-600"
                                >
                                  <TrashIcon className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </StepCard>
                  )}

                  {/* ── STEP 4: Additional ── */}
                  {step === 4 && (
                    <StepCard
                      stepNumber={4}
                      title="Additional Information"
                      description="Amenities, highlights, and nearby places."
                      isOpen={openSections[4]}
                      toggle={() => setOpenSections((p) => ({ ...p, 4: !p[4] }))}
                    >
                      <div className="space-y-5">
                        {/* Amenities */}
                        <div>
                          <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5">
                            <ShieldCheckIcon className="w-4 h-4 text-blue-600" />
                            Amenities & Features
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {AMENITIES.map((a) => {
                              const active = amenities.includes(a);
                              return (
                                <button
                                  key={a}
                                  type="button"
                                  onClick={() => toggleAmenity(a)}
                                  className={`px-3 py-1.5 rounded-full text-[11px] border flex items-center gap-1.5 transition-all ${
                                    active
                                      ? "bg-blue-600 text-white border-blue-600"
                                      : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
                                  }`}
                                >
                                  <BoltIcon className="w-3 h-3" />
                                  {a}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Description */}
                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-1.5">Description</p>
                          <RichTextEditor
                            placeholder="Describe your plot..."
                            value={form.description}
                            onChange={(c) => updateField("description", c)}
                            height={180}
                          />
                        </div>

                        {/* Highlights */}
                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-1.5">Highlights</p>
                          <RichTextEditor
                            placeholder="Corner plot, park-facing, DTCP approved, etc."
                            value={form.highlights}
                            onChange={(c) => updateField("highlights", c)}
                            height={150}
                          />
                        </div>

                        {/* Nearby */}
                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-1.5">Nearby Locations</p>
                          <RichTextEditor
                            placeholder="Schools, hospitals, metro, IT parks..."
                            value={form.nearbyLocations}
                            onChange={(c) => updateField("nearbyLocations", c)}
                            height={150}
                          />
                        </div>
                      </div>
                    </StepCard>
                  )}

                  {/* ── STEP 5: Location ── */}
                  {step === 5 && (
                    <StepCard
                      stepNumber={5}
                      title="Location Details"
                      description="Exact location helps buyers find your plot easily."
                      isOpen={openSections[5]}
                      toggle={() => setOpenSections((p) => ({ ...p, 5: !p[5] }))}
                    >
                      <div className="space-y-3">
                        <div>
                          <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5">
                            <MapPinIcon className="w-4 h-4 text-blue-600" />
                            City *
                          </p>
                          <LocationAutocomplete
                            value={form.city}
                            onChange={(v) => updateField("city", v)}
                            onSelect={(item) => updateField("city", item.city)}
                            mode="city"
                            placeholder="Enter city name..."
                            inputClassName="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                          />
                          {errors.city && <p className="text-[11px] text-red-500 mt-1">{errors.city}</p>}
                        </div>

                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-1.5">Locality / Area *</p>
                          <LocationAutocomplete
                            value={form.locality}
                            onChange={(v) => updateField("locality", v)}
                            onSelect={(item) => updateField("locality", item.locality || item.label)}
                            mode="locality"
                            cityFilter={form.city || null}
                            placeholder="Enter locality / area..."
                            inputClassName="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                          />
                          {errors.locality && <p className="text-[11px] text-red-500 mt-1">{errors.locality}</p>}
                        </div>

                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-1.5">Street Address *</p>
                          <input
                            type="text"
                            className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                            value={form.streetAddress}
                            onChange={(e) => updateField("streetAddress", e.target.value)}
                          />
                          {errors.streetAddress && <p className="text-[11px] text-red-500 mt-1">{errors.streetAddress}</p>}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1.5">Landmark (optional)</p>
                            <input
                              type="text"
                              className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                              value={form.landmark}
                              onChange={(e) => updateField("landmark", e.target.value)}
                            />
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1.5">Pincode</p>
                            <input
                              type="text"
                              className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                              value={form.pincode}
                              onChange={(e) => updateField("pincode", e.target.value)}
                            />
                          </div>
                        </div>

                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-1.5">Google Map Link (optional)</p>
                          <input
                            type="text"
                            className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                            placeholder="Paste Google Maps URL"
                            value={form.mapUrl}
                            onChange={(e) => updateField("mapUrl", e.target.value)}
                          />
                        </div>

                        {/* Interactive map pin */}
                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-2">📍 Pin Plot Location on Map</p>
                          <p className="text-xs text-gray-500 mb-3">
                            Use GPS or click on the map to mark your plot location.
                          </p>
                          <LocationPicker
                            onLocationSelect={({ latitude, longitude }) => {
                              updateField("latitude", latitude);
                              updateField("longitude", longitude);
                            }}
                            initialLat={form.latitude || 8.7139}
                            initialLng={form.longitude || 77.7567}
                          />
                          {form.latitude && form.longitude && (
                            <div className="mt-2 text-xs text-green-600 bg-green-50 px-3 py-2 rounded-lg">
                              ✓ Location saved: {parseFloat(form.latitude).toFixed(6)}, {parseFloat(form.longitude).toFixed(6)}
                            </div>
                          )}
                        </div>
                      </div>
                    </StepCard>
                  )}

                  {/* ── STEP 6: Contact ── */}
                  {step === 6 && (
                    <StepCard
                      stepNumber={6}
                      title="Contact Details"
                      description="Let buyers know how to reach you."
                      isOpen={openSections[6]}
                      toggle={() => setOpenSections((p) => ({ ...p, 6: !p[6] }))}
                    >
                      <div className="space-y-4">
                        {/* Quick summary card */}
                        <div className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 text-xs text-gray-700 space-y-1">
                          <p className="font-semibold text-gray-900 text-sm flex items-center gap-1.5">
                            <HomeIcon className="w-4 h-4 text-blue-600" />
                            {form.plotTitle || "Untitled Plot"}
                          </p>
                          <p>{form.city && form.locality ? `${form.locality}, ${form.city}` : "Location not filled yet"}</p>
                          <p>{form.expectedPrice ? `₹${form.expectedPrice}` : "Price not set"}</p>
                          <p className="text-[11px] text-gray-500">
                            {form.plotArea ? `${form.plotArea} sq ft • ${form.plotType || "Plot"}` : "Add specs in previous steps"}
                          </p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5">
                              <UserIcon className="w-4 h-4 text-blue-600" />
                              Owner Name *
                            </p>
                            <input
                              type="text"
                              className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                              value={form.ownerName}
                              onChange={(e) => updateField("ownerName", e.target.value)}
                            />
                            {errors.ownerName && <p className="text-[11px] text-red-500 mt-1">{errors.ownerName}</p>}
                          </div>
                          <div>
                            <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5">
                              <PhoneIcon className="w-4 h-4 text-blue-600" />
                              Mobile Number *
                            </p>
                            <input
                              type="text"
                              maxLength={10}
                              className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                              value={form.mobileNumber}
                              onChange={(e) => updateField("mobileNumber", e.target.value.replace(/\D/g, "").slice(0, 10))}
                              placeholder="10-digit mobile number"
                            />
                            {errors.mobileNumber && <p className="text-[11px] text-red-500 mt-1">{errors.mobileNumber}</p>}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1.5">Alternate Number</p>
                            <input
                              type="text"
                              maxLength={10}
                              className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                              value={form.altNumber}
                              onChange={(e) => updateField("altNumber", e.target.value.replace(/\D/g, "").slice(0, 10))}
                              placeholder="10-digit alternate number"
                            />
                            {errors.altNumber && <p className="text-[11px] text-red-500 mt-1">{errors.altNumber}</p>}
                          </div>
                          <div>
                            <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5">
                              <EnvelopeIcon className="w-4 h-4 text-blue-600" />
                              Email *
                            </p>
                            <input
                              type="email"
                              className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                              value={form.email}
                              onChange={(e) => updateField("email", e.target.value)}
                            />
                            {errors.email && <p className="text-[11px] text-red-500 mt-1">{errors.email}</p>}
                          </div>
                        </div>

                        <div>
                          <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5">
                            <ClockIcon className="w-4 h-4 text-blue-600" />
                            Preferred Contact Time
                          </p>
                          <input
                            type="text"
                            className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                            placeholder="e.g. 10 AM – 7 PM"
                            value={form.preferredContactTime}
                            onChange={(e) => updateField("preferredContactTime", e.target.value)}
                          />
                        </div>
                      </div>
                    </StepCard>
                  )}

                  {/* Navigation buttons */}
                  <div className="mt-4 flex items-center justify-between gap-2 pt-2 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={goBack}
                      disabled={step === 1}
                      className={`text-xs font-medium px-4 py-2 rounded-full border transition ${
                        step === 1
                          ? "border-gray-100 text-gray-300 cursor-not-allowed"
                          : "border-gray-200 text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      Back
                    </button>

                    {step < TOTAL_STEPS ? (
                      <button
                        type="button"
                        onClick={goNext}
                        className="ml-auto text-xs sm:text-sm font-semibold px-5 py-2.5 rounded-full bg-blue-600 text-white shadow-sm hover:bg-blue-700 transition"
                      >
                        Next
                      </button>
                    ) : (
                      <button
                        type="submit"
                        disabled={submitting}
                        className="ml-auto text-xs sm:text-sm font-semibold px-6 py-2.5 rounded-full bg-green-600 text-white shadow-sm hover:bg-green-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        {submitting ? "Publishing…" : "Publish Plot"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Duration / payment modal after successful create */}
      {showDurationModal && (
        <DurationSelectionModal
          isOpen={showDurationModal}
          onClose={() => {
            setShowDurationModal(false);
            setActiveTab("posted");
          }}
          listingId={createdListingId}
          onSuccess={() => {
            setShowDurationModal(false);
            setActiveTab("posted");
            window.location.reload();
          }}
          planType="PLOT"
        />
      )}
    </>
  );
};

// ─── Page wrapper (Layout + PlotForm) ────────────────────────────────────────
const PlotPage = () => (
  <Layout>
    <PlotForm />
  </Layout>
);

export default PlotPage;
